import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import Layout from "@/components/app/Layout";
import StorageWizard from "@/components/app/StorageWizard";
import TwilioWizard from "@/components/app/TwilioWizard";
import OrgChart from "@/components/app/OrgChart";
import EmailDeliveryCard from "@/components/app/EmailDeliveryCard";
import ClientMultiSelect, { CLIENT_SCOPED_ROLES } from "@/components/app/ClientMultiSelect";
import { useCachedGet } from "@/lib/cache";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/perm";
import PermissionsMatrix from "@/components/app/PermissionsMatrix";
import ReportFieldsCard from "@/components/app/ReportFieldsCard";
import { toast } from "sonner";
import { Save, Upload, Trash2, Image as ImgIcon, Loader2, UserPlus, Copy, X as XIcon, Crown, User as UserIcon, Network, List, ShieldCheck, FileText, Building2, Plug, Users, SlidersHorizontal } from "lucide-react";

// New-role → nice-label map used across the Team panel and manager dropdowns.
const ROLE_LABELS = {
  ta_recruiter: "TA Recruiter",
  ta_lead: "TA Lead",
  tag_lead: "TAG Lead",
  tag_head: "TAG Head",
  tenant_admin: "Tenant Admin",
  platform_owner: "Platform Owner",
};
// For each invited role, which roles can be its manager. Mirrors backend.
const VALID_MANAGER_ROLES = {
  ta_recruiter: ["ta_lead", "tag_lead", "tag_head", "tenant_admin"],
  ta_lead:      ["tag_lead", "tag_head", "tenant_admin"],
  tag_lead:     ["tag_head", "tenant_admin"],
  tag_head:     ["tenant_admin"],
  tenant_admin: [],
};
const REQUIRES_MANAGER = new Set(["ta_recruiter", "ta_lead", "tag_lead"]);
const ADMIN_ROLES = new Set(["tenant_admin", "platform_owner", "owner", "admin"]);

function roleLabel(r) { return ROLE_LABELS[r] || r; }
function isAdmin(user) { return !!user && ADMIN_ROLES.has(user.role); }

const TABS = [
  { key: "workspace", label: "Workspace", Icon: Building2, admin: true },
  { key: "integrations", label: "Integrations", Icon: Plug, admin: true },
  { key: "team", label: "Team", Icon: Users },
  { key: "permissions", label: "Role Permissions", Icon: SlidersHorizontal, admin: true },
];
const TAB_HINTS = {
  workspace: "Your logo and workspace name appear on every candidate report and across the recruiter UI.",
  integrations: "Connect phone calling, email delivery and recording storage for this workspace.",
  team: "Invite your team, set reporting lines and assign clients.",
  permissions: "Choose what each role can do and which jobs it can see.",
};

function SettingsTabs({ tabs, active, onChange }) {
  if (tabs.length < 2) return null;
  return (
    <div className="sticky top-0 z-10 -mx-1 px-1 py-2 mb-5 bg-[color:var(--bg-void)]" data-testid="settings-tabs">
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
        {tabs.map(({ key, label, Icon }) => (
          <button key={key} type="button" onClick={() => onChange(key)} data-testid={`settings-tab-${key}`} aria-selected={active === key} role="tab"
            className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm px-3.5 py-2 rounded-lg transition-colors ${active === key ? "bg-indigo-600 text-white font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user, tenant: ctxTenant, setTenant: setCtxTenant } = useAuth();
  const canManage = isAdmin(user);
  const canAssignClients = can(user, "clients.assign");
  const canReassign = (m) => canManage || (can(user, "team.reassign") && m.manager_id === user?.id);
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter((t) => !t.admin || canManage);
  const activeTab = tabs.some((t) => t.key === params.get("tab")) ? params.get("tab") : tabs[0].key;
  const setTab = (key) => setParams({ tab: key }, { replace: true });
  const [clientsData] = useCachedGet("/clients");
  const clients = clientsData || [];
  const clientName = (id) => clients.find(c => c.id === id)?.name;
  const [tenant, setTenant] = useState(ctxTenant);
  const [name, setName] = useState(ctxTenant?.name || "");
  const [logo, setLogo] = useState(ctxTenant?.logo_data_url || null);
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState({ members: [], invites: [] });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "ta_recruiter", manager_id: "", company_name: "", client_ids: [] });
  const [inviting, setInviting] = useState(false);
  const [teamView, setTeamView] = useState("list"); // "list" | "org"
  const fileRef = useRef(null);

  useEffect(() => {
    // Hydrate from context if present, otherwise fetch (handles the rare case
    // the user lands directly on /app/settings before context resolved).
    if (ctxTenant) { setTenant(ctxTenant); setName(ctxTenant.name || ""); setLogo(ctxTenant.logo_data_url || null); }
    else { api.get("/tenant/me").then((r) => { setTenant(r.data); setName(r.data.name || ""); setLogo(r.data.logo_data_url || null); setCtxTenant(r.data); }); }
    loadTeam();
  }, [ctxTenant, setCtxTenant]);
  const loadTeam = () => api.get("/tenant/members").then(r => setTeam(r.data)).catch(() => setTeam({ members: [], invites: [] }));

  // Managers eligible to receive the currently selected invite role.
  const eligibleManagers = useMemo(() => {
    const allowed = VALID_MANAGER_ROLES[inviteForm.role] || [];
    return (team.members || []).filter(m => allowed.includes(m.role) || (m.role === "owner" && allowed.includes("tenant_admin")) || (m.role === "admin" && allowed.includes("tenant_admin")));
  }, [inviteForm.role, team.members]);

  // Reset manager when role changes to one that doesn't need (or no longer accepts) current manager.
  useEffect(() => {
    if (!REQUIRES_MANAGER.has(inviteForm.role)) {
      setInviteForm((f) => ({ ...f, manager_id: "" }));
    }
  }, [inviteForm.role]);

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.size > 500 * 1024) { toast.error("Logo too large — max 500 KB"); return; }
    const reader = new FileReader(); reader.onload = () => setLogo(reader.result); reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name };
      if (logo !== tenant?.logo_data_url) payload.logo_data_url = logo || "";
      const { data } = await api.patch("/tenant/me", payload);
      setTenant(data);
      setCtxTenant(data); // sync sidebar logo without a reload
      toast.success("Workspace updated");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const removeLogo = () => { setLogo(null); toast.info("Click Save to remove the logo"); };

  const invite = async (e) => {
    e.preventDefault();
    if (REQUIRES_MANAGER.has(inviteForm.role) && !inviteForm.manager_id) {
      toast.error(`A ${roleLabel(inviteForm.role)} must be assigned to a manager`);
      return;
    }
    if (CLIENT_SCOPED_ROLES.has(inviteForm.role) && clients.length > 0 && inviteForm.client_ids.length === 0) {
      toast.error(`Pick at least one client this ${roleLabel(inviteForm.role)} will work for`);
      return;
    }
    setInviting(true);
    try {
      const payload = { email: inviteForm.email, role: inviteForm.role };
      if (CLIENT_SCOPED_ROLES.has(inviteForm.role)) payload.client_ids = inviteForm.client_ids;
      if (inviteForm.manager_id) payload.manager_id = inviteForm.manager_id;
      if (inviteForm.role === "tenant_admin" && inviteForm.company_name) payload.company_name = inviteForm.company_name;
      const { data } = await api.post("/tenant/members/invite", payload);
      const link = `${window.location.origin}/team-invite/${data.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success(data.new_tenant_name ? `Invite sent — they'll get their own "${data.new_tenant_name}" workspace` : "Invite created — link copied");
      setInviteForm({ email: "", role: "ta_recruiter", manager_id: "", company_name: "", client_ids: [] }); loadTeam();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setInviting(false); }
  };
  const copyInvite = (token) => { navigator.clipboard.writeText(`${window.location.origin}/team-invite/${token}`).then(() => toast.success("Link copied")); };
  const revokeInvite = async (id) => { if (!window.confirm("Revoke this pending invite?")) return; try { await api.delete(`/tenant/invites/${id}`); toast.success("Revoked"); loadTeam(); } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } };
  const changeRole = async (uid, role) => { try { await api.patch(`/tenant/members/${uid}`, { role }); toast.success("Role updated"); loadTeam(); } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } };
  const reassignManager = async (uid, managerId) => { try { await api.patch(`/tenant/members/${uid}`, { manager_id: managerId || null }); toast.success("Manager updated"); loadTeam(); } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } };
  const assignClients = async (uid, client_ids) => { try { await api.patch(`/tenant/members/${uid}`, { client_ids }); loadTeam(); } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } };
  const removeMember = async (uid, name) => { if (!window.confirm(`Remove ${name} from the workspace?`)) return; try { await api.delete(`/tenant/members/${uid}`); toast.success("Removed"); loadTeam(); } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } };

  const memberById = useMemo(() => Object.fromEntries((team.members || []).map(m => [m.id, m])), [team.members]);

  return (
    <Layout>
      <div className="max-w-4xl">
        <div className="mb-5">
          <h1 className="font-display text-3xl font-bold">Settings</h1>
          <p className="text-sm text-slate-400 mt-1">{TAB_HINTS[activeTab]}</p>
        </div>

        {params.get("tab") === "plan" && <Navigate to="/app/licenses" replace />}
        <SettingsTabs tabs={tabs} active={activeTab} onChange={setTab} />

        {activeTab === "workspace" && (
        <div className="tp-card p-6 mb-6" data-testid="tenant-settings">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Company Name</div>
          <input data-testid="tenant-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />

          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 mt-6">Company Logo</div>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl border border-[color:var(--bd-sub)] bg-[color:var(--bg-elev)] grid place-items-center overflow-hidden" data-testid="logo-preview">
              {logo ? <img src={logo} alt="Logo" className="max-w-full max-h-full object-contain" /> : <ImgIcon className="w-8 h-8 text-slate-600" />}
            </div>
            <div className="flex flex-col gap-2">
              <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onPick} data-testid="logo-file" />
              <button onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-white/5"><Upload className="w-4 h-4"/> {logo ? "Replace logo" : "Upload logo"}</button>
              {logo && <button onClick={removeLogo} data-testid="logo-remove" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4"/> Remove</button>}
              <div className="text-xs text-slate-500">PNG or JPG, max 500 KB. Best at ~200×60.</div>
            </div>
          </div>
          <button data-testid="save-tenant" onClick={save} disabled={saving} className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save changes
          </button>
        </div>
        )}
        {activeTab === "workspace" && tenant && (
          <ReportFieldsCard key={tenant.id} tenant={tenant} onSaved={(d) => { setTenant(d); setCtxTenant(d); }} />
        )}

        {activeTab === "integrations" && (
          <>
            <div className="mb-6"><TwilioWizard /></div>
            <div className="mb-6"><EmailDeliveryCard /></div>
            <div className="mb-6"><StorageWizard /></div>
          </>
        )}

        {activeTab === "team" && (
        <div className="tp-card p-6" data-testid="team-panel">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="font-display text-lg font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-600"/> Team &amp; Permissions</div>
              <div className="text-xs text-slate-400">Invite your TA org and assign reporting lines. Managers see every job/candidate created by their reports.</div>
              <a href="/recruitsift-teams-permissions-guide.pdf" target="_blank" rel="noopener noreferrer" download data-testid="permissions-guide-link"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                <FileText className="w-3.5 h-3.5"/> Roles &amp; permissions guide (PDF)
              </a>
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button data-testid="view-list" onClick={() => setTeamView("list")} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md ${teamView === "list" ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-500"}`}><List className="w-3.5 h-3.5"/> List</button>
              <button data-testid="view-org" onClick={() => setTeamView("org")} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md ${teamView === "org" ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-500"}`}><Network className="w-3.5 h-3.5"/> Org Chart</button>
            </div>
          </div>

          {canManage && (
            <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-6" data-testid="invite-form">
              <input data-testid="invite-email" value={inviteForm.email} onChange={(e)=>setInviteForm({...inviteForm, email: e.target.value})} type="email" required placeholder="teammate@yourcompany.com" className="sm:col-span-4 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              <select data-testid="invite-role" value={inviteForm.role} onChange={(e)=>setInviteForm({...inviteForm, role: e.target.value})} className="sm:col-span-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)]">
                <option value="ta_recruiter">TA Recruiter</option>
                <option value="ta_lead">TA Lead</option>
                <option value="tag_lead">TAG Lead</option>
                <option value="tag_head">TAG Head</option>
                <option value="tenant_admin">Tenant Admin</option>
              </select>
              {VALID_MANAGER_ROLES[inviteForm.role]?.length > 0 && (
                <select data-testid="invite-manager" value={inviteForm.manager_id} onChange={(e)=>setInviteForm({...inviteForm, manager_id: e.target.value})} className="sm:col-span-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)]" required={REQUIRES_MANAGER.has(inviteForm.role)}>
                  <option value="">{REQUIRES_MANAGER.has(inviteForm.role) ? "Select manager…" : "No manager"}</option>
                  {eligibleManagers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} — {roleLabel(m.role === "owner" || m.role === "admin" ? "tenant_admin" : m.role)}</option>
                  ))}
                </select>
              )}
              {inviteForm.role === "tenant_admin" && user?.role === "platform_owner" && (
                <input data-testid="invite-company-name" value={inviteForm.company_name} onChange={(e)=>setInviteForm({...inviteForm, company_name: e.target.value})} placeholder="Company Name (new workspace)" className="sm:col-span-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" title="They'll be the admin of this brand-new workspace. Leave blank to join your workspace." />
              )}
              {CLIENT_SCOPED_ROLES.has(inviteForm.role) && (
                <div className="sm:col-span-10">
                  <ClientMultiSelect clients={clients} value={inviteForm.client_ids} onChange={(client_ids) => setInviteForm({ ...inviteForm, client_ids })} testid="invite-clients" placeholder="Clients this person works for" />
                </div>
              )}
              <button data-testid="invite-submit" disabled={inviting} className="sm:col-span-2 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60"><UserPlus className="w-4 h-4"/> {inviting ? "Sending…" : "Invite"}</button>
            </form>
          )}

          {teamView === "list" ? (
            <>
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Active Members ({team.members?.length || 0})</div>
                <div className="space-y-2">
                  {team.members.map(m => {
                    const nR = (m.role === "owner" || m.role === "admin") ? "tenant_admin" : m.role;
                    const mgr = m.manager_id ? memberById[m.manager_id] : null;
                    return (
                      <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)]" data-testid={`member-row-${m.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-indigo-500/20 border border-indigo-500/40 grid place-items-center flex-shrink-0">
                            {nR === "tenant_admin" || nR === "platform_owner" ? <Crown className="w-4 h-4 text-amber-300"/> : <UserIcon className="w-4 h-4 text-indigo-300"/>}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{m.name}</div>
                            <div className="text-xs text-slate-500 truncate">{m.email}</div>
                            {mgr && <div className="text-[11px] text-slate-500">reports to <span className="text-slate-700 font-medium">{mgr.name}</span></div>}
                            {CLIENT_SCOPED_ROLES.has(nR) && (
                              <div className="text-[11px] text-slate-500 truncate" data-testid={`member-clients-${m.id}`}>
                                {(m.client_ids || []).length ? <>clients: <span className="text-slate-700 font-medium">{m.client_ids.map(clientName).filter(Boolean).join(", ")}</span></> : <span className="text-amber-600">No clients assigned — sees all clients</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="tp-chip tp-chip-indigo text-[11px]" data-testid={`role-label-${m.id}`}>{roleLabel(nR)}</span>
                          {canAssignClients && CLIENT_SCOPED_ROLES.has(nR) && (
                            <ClientMultiSelect compact clients={clients} value={m.client_ids || []} onChange={(ids) => assignClients(m.id, ids)} testid={`clients-${m.id}`} placeholder="Assign clients" />
                          )}
                          {canManage && (
                            <select data-testid={`role-${m.id}`} value={nR} onChange={(e) => changeRole(m.id, e.target.value)} className="text-xs px-2 py-1 rounded bg-white border border-[color:var(--bd-sub)]">
                              <option value="ta_recruiter">TA Recruiter</option>
                              <option value="ta_lead">TA Lead</option>
                              <option value="tag_lead">TAG Lead</option>
                              <option value="tag_head">TAG Head</option>
                              <option value="tenant_admin">Tenant Admin</option>
                            </select>
                          )}
                          {canReassign(m) && VALID_MANAGER_ROLES[nR]?.length > 0 && (
                                <select data-testid={`mgr-${m.id}`} value={m.manager_id || ""} onChange={(e) => reassignManager(m.id, e.target.value)} className="text-xs px-2 py-1 rounded bg-white border border-[color:var(--bd-sub)] max-w-[160px]">
                                  <option value="">{REQUIRES_MANAGER.has(nR) ? "Select manager" : "No manager"}</option>
                                  {team.members.filter(x => x.id !== m.id && (VALID_MANAGER_ROLES[nR] || []).includes((x.role === "owner" || x.role === "admin") ? "tenant_admin" : x.role)).map(x => (
                                    <option key={x.id} value={x.id}>{x.name}</option>
                                  ))}
                                </select>
                          )}
                          {canManage && (
                            <button data-testid={`remove-${m.id}`} onClick={() => removeMember(m.id, m.name)} className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400" title="Remove"><Trash2 className="w-4 h-4"/></button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {canManage && team.invites.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Pending Invites</div>
                  <div className="space-y-2">
                    {team.invites.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg bg-[color:var(--bg-elev)] border border-amber-500/20" data-testid={`invite-row-${inv.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{inv.email}</div>
                          <div className="text-xs text-amber-400/80">pending · {roleLabel(inv.role)}{inv.manager_id && memberById[inv.manager_id] ? ` · reports to ${memberById[inv.manager_id].name}` : ""}{(inv.client_ids || []).length ? ` · ${inv.client_ids.map(clientName).filter(Boolean).join(", ")}` : ""}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button data-testid={`copy-invite-${inv.id}`} onClick={() => copyInvite(inv.token)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[color:var(--bd-hi)] hover:bg-white/5"><Copy className="w-3.5 h-3.5"/> Copy link</button>
                          <button data-testid={`revoke-invite-${inv.id}`} onClick={() => revokeInvite(inv.id)} className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400" title="Revoke"><XIcon className="w-4 h-4"/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <OrgChart />
          )}
        </div>
        )}
        {activeTab === "permissions" && <PermissionsMatrix />}
      </div>
    </Layout>
  );
}
